import { useQuery } from "@tanstack/react-query";

interface MarketData {
  id?: string;
  slug: string;
  question: string;
  description: string | null;
  image: string | null;
  endDate: string | null;
  eventStartTime?: string | null;
  closed: boolean;
  active: boolean;
  acceptingOrders: boolean;
  clobTokenIds: string[];
  conditionId: string | null;
  outcomes: string[];
  outcomePrices: number[];
  volume: number;
  volume24hr: number;
  liquidity: number;
  orderPriceMinTickSize: number;
  orderMinSize: number;
  priceToBeat: number | null;
  resolutionSource: string | null;
  negRisk: boolean;
  oneDayPriceChange: number | null;
  oneHourPriceChange: number | null;
  competitive: number | null;
  createdAt: string | null;
  resolvedBy: string | null;
  seriesSlug?: string | null;
  parentEventSlug?: string | null;
  parentEventId?: string;
  seriesId?: string;
  source?: "kalshi" | "polymarket" | "predict";
  eventTitle?: string | null;
  eventCategory?: string | null;
  marketTitle?: string | null;
  relatedMarkets?: Array<{ id: string; question: string; slug: string; eventTicker: string; marketTicker: string; outcomePrices: number[]; volume: number; image: string | null }>;
  rules?: string | null;
  rulesSecondary?: string | null;
  settlementTimerSeconds?: number | null;
  canCloseEarly?: boolean;
  earlyCloseCondition?: string | null;
  eventTicker?: string | null;
  openInterest?: number;
}

interface UmaDispute {
  state: string;
  ooRequestUrl?: string | null;
  originatingChainId?: number | null;
  originatingOracleType?: string | null;
}

interface MarketPageData {
  market: MarketData;
  umaDispute: UmaDispute | null;
}

async function fetchMarketPageData(slug: string, isKalshi: boolean): Promise<MarketPageData> {
  if (isKalshi) {
    const response = await fetch(`/api/markets/kalshi/${slug}`);
    if (!response.ok) throw new Error("Failed to fetch Kalshi market data");
    const data = await response.json();
    const market = data.market;
    if (!market) throw new Error("Market not found");

    const eventTicker = market.sourceData?.kalshi?.eventTicker || market.eventTicker || "";
    return {
      market: {
        slug,
        question: market.eventTitle
          ? `${market.eventTitle}: ${market.marketTitle || market.title}`
          : (market.title || market.question || ""),
        description: market.description || market.rules || null,
        image: market.image || market.imageUrl || null,
        endDate: market.endDate || market.closeTime || null,
        closed: market.status === "closed" || market.status === "settled" || market.status === "finalized",
        active: market.status === "open" || market.status === "active",
        acceptingOrders: market.status === "open" || market.status === "active",
        clobTokenIds: [],
        conditionId: slug.toUpperCase(),
        outcomes: ["Yes", "No"],
        outcomePrices: [market.yesPrice || 0.5, market.noPrice || 0.5],
        volume: market.volume || 0,
        volume24hr: market.volume24h || market.volume24hr || 0,
        liquidity: market.liquidity || 0,
        openInterest: market.openInterest || 0,
        orderPriceMinTickSize: 0.01,
        orderMinSize: 1,
        priceToBeat: null,
        resolutionSource: null,
        negRisk: false,
        oneDayPriceChange: market.priceChange24h || null,
        oneHourPriceChange: null,
        competitive: null,
        createdAt: market.openTime || null,
        resolvedBy: null,
        parentEventSlug: eventTicker ? eventTicker.toLowerCase() : null,
        source: "kalshi",
        eventTitle: market.eventTitle || null,
        eventCategory: market.eventCategory || null,
        marketTitle: market.marketTitle || market.title || null,
        relatedMarkets: market.relatedMarkets || [],
        rules: market.description || market.rules || null,
        rulesSecondary: market.rulesSecondary || null,
        settlementTimerSeconds: market.settlementTimerSeconds ?? null,
        canCloseEarly: market.canCloseEarly || false,
        earlyCloseCondition: market.earlyCloseCondition || null,
        eventTicker,
      },
      umaDispute: null,
    };
  }

  // Polymarket: fetch market and UMA dispute in parallel
  const [response, umaRes] = await Promise.all([
    fetch(`/api/polymarket/market/${slug}`),
    fetch(`/api/uma/market/${slug}`).catch(() => null),
  ]);

  let umaDispute: UmaDispute | null = null;
  if (umaRes?.ok) {
    const umaData = await umaRes.json().catch(() => null);
    if (umaData?.success && umaData.data) {
      umaDispute = umaData.data;
    }
  }

  // PM crypto up/down 5m/15m markets rotate every interval — when an
  // event page loads a slug that hasn't surfaced in gamma yet (or has
  // already been garbage-collected) the route 404s. Throwing here put
  // React Query into a permanent error state attached to the page,
  // which caused commitLayoutEffect on every subsequent render to log
  // the failure into ErrorState and shifted the hook count for the
  // surrounding tree → minified React error #300 storm.
  // Returning a null market lets the consumer (EventClient) fall back
  // to its own initialData/eventData snapshot — no network retry
  // pressure, no boundary trigger.
  if (!response.ok) {
    return { market: null as unknown as MarketData, umaDispute };
  }
  const data = await response.json();

  // Parse clobTokenIds
  let tokenIds: string[] = [];
  if (data.clobTokenIds) {
    try {
      tokenIds = typeof data.clobTokenIds === "string"
        ? JSON.parse(data.clobTokenIds)
        : data.clobTokenIds;
    } catch {
      const cleaned = data.clobTokenIds.replace(/[\[\]"]/g, "");
      tokenIds = cleaned.split(",").map((id: string) => id.trim());
    }
  }

  // Use priceToBeat from API (computed by route handler), fallback to extracting from question
  const priceToBeat = (() => {
    if (data.priceToBeat != null && data.priceToBeat > 0) return data.priceToBeat;
    const q = data.question || "";
    const match = q.match(/\$([0-9,]+(?:\.\d+)?)/);
    if (!match) return null;
    const price = parseFloat(match[1].replace(/,/g, ""));
    return isNaN(price) ? null : price;
  })();

  return {
    market: { ...data, clobTokenIds: tokenIds, priceToBeat },
    umaDispute,
  };
}

/**
 * React Query hook for market detail page data.
 * Uses initialData from SSR to avoid redundant refetch on mount.
 * Fetches market data + UMA dispute in parallel.
 */
export function useMarketPageQuery(
  slug: string,
  isKalshi: boolean,
  initialData?: MarketData | null,
  /**
   * Skip the network fetch entirely. EventClient passes this for
   * Hyperliquid events because /api/polymarket/market/:slug 404s on HL
   * slugs (no Polymarket record exists), the React Query auto-retry
   * keeps the failed query attached to the page render, and the parent
   * lands in a #300 re-render storm. HL detail data already comes from
   * SSR initialData built in transformToEventData on
   * /hyperliquid/event/[slug]/page.tsx — no API call needed.
   */
  skipFetch = false,
) {
  // Detect SSR data that needs immediate refetch
  const isCryptoUpDown = slug.includes("updown") || initialData?.question?.toLowerCase().includes("up or down");
  const needsImmediateRefetch =
    // Missing priceToBeat for crypto up/down
    (isCryptoUpDown && (!initialData?.priceToBeat || initialData.priceToBeat <= 0)) ||
    // Missing clobTokenIds (needed for WS price streaming)
    (!initialData?.clobTokenIds?.length) ||
    // Crypto up/down markets rotate every 5min — SSR cache (5min) can be stale
    isCryptoUpDown;

  return useQuery<MarketPageData, Error>({
    queryKey: ["market-page", isKalshi ? "kalshi" : "polymarket", slug],
    // When skipFetch is set we still want React Query to honor initialData,
    // but never hit the network. queryFn would be unreachable with
    // enabled:false, but we provide a defensive resolver so any forced
    // refetch returns the cached snapshot instead of crashing.
    queryFn: () =>
      skipFetch
        ? Promise.resolve(
            initialData
              ? { market: initialData, umaDispute: null }
              : ({ market: null, umaDispute: null } as unknown as MarketPageData),
          )
        : fetchMarketPageData(slug, isKalshi),
    enabled: !!slug && !skipFetch,
    initialData: initialData
      ? { market: initialData, umaDispute: null }
      : undefined,
    initialDataUpdatedAt: initialData ? 0 : undefined,
    staleTime: needsImmediateRefetch ? 0 : 30_000,
    gcTime: 5 * 60_000,
    retry: false,
  });
}

export type { MarketData, MarketPageData, UmaDispute };
